import { useMemo, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { get } from '@truefit/http-utils';

import {
  KeyboardShortcut,
  KeyboardShortcutGroup,
  KeyboardShortcutGroupOrderNumber,
  KeyboardShortcutGroupTitles,
  KeyboardShortcutsList,
} from '../constants';
import useKeyboardShortcutsDialog from '../hooks/useKeyboardShortcutsDialog';
import KeyboardCmd from './KeyboardCmd';
import { MouseDiagram } from '@/features/games/components';
import useCurrentGameId from '../hooks/useCurrentGameId';
import { isUUID } from 'validator';
import { CameraModel } from '@/features/camera/types/CameraSettingsModel';

// Simplified type for camera shortcuts display
type CameraShortcutDisplay = {
  keys: string[];
  command: string;
};

type Props = {
  shortcuts?: Record<KeyboardShortcutGroup, KeyboardShortcut[]>;
};

const KeyboardShortcutsDialog = ({ shortcuts = KeyboardShortcutsList }: Props) => {
  const { showing, hide } = useKeyboardShortcutsDialog();
  const gameId = useCurrentGameId();
  const isOnGamePage = gameId ? isUUID(gameId) : false;

  const [cameras, setCameras] = useState<CameraModel[]>([]);
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);

  // Fetch cameras directly when dialog opens on a game page
  useEffect(() => {
    if (showing && isOnGamePage && gameId) {
      setIsLoadingCameras(true);
      get<CameraModel[]>(`/v1.0/games/${gameId}/cameras`)
        .then(({ data }) => {
          setCameras(data || []);
        })
        .catch((error) => {
          console.error('[KeyboardShortcutsDialog] Failed to fetch cameras:', error);
          setCameras([]);
        })
        .finally(() => {
          setIsLoadingCameras(false);
        });
    }
  }, [showing, isOnGamePage, gameId]);

  // Generate dynamic camera shortcuts from available cameras
  const cameraShortcuts: CameraShortcutDisplay[] = useMemo(() => {
    if (!cameras || cameras.length === 0) {
      return [];
    }

    return cameras.slice(0, 9).map((camera, index) => ({
      keys: [(index + 1).toString()],
      command: camera.displayName || `Camera ${index + 1}`,
    }));
  }, [cameras]);

  return (
    <Dialog
      className="tgph"
      open={showing}
      onClose={hide}
      maxWidth={false}
      disablePortal
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '8px',
            textAlign: 'center',
          },
        },
        backdrop: {
          sx: {
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <DialogTitle sx={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.95rem' }}>
        Keyboard Shortcuts
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 0 }}>
        <Stack direction="row" spacing={8} sx={{ mt: 2 }}>
          {(Object.keys(shortcuts) as KeyboardShortcutGroup[])
            .sort(
              (a, b) => KeyboardShortcutGroupOrderNumber[a] - KeyboardShortcutGroupOrderNumber[b],
            )
            .filter((group) => group !== 'General')
            .map((group) => (
              <Box key={group}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {KeyboardShortcutGroupTitles[group]}
                </Typography>
                {shortcuts[group].map((s) => (
                  <Stack key={s.command} direction="row" spacing={1} sx={{ mt: 1 }}>
                    <KeyboardCmd keys={s.keys} />

                    <Box sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem' }}>{s.command}</Box>
                  </Stack>
                ))}
              </Box>
            ))}

          {/* Dynamic Camera Shortcuts - only shown on game pages */}
          {isOnGamePage && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                Camera Selection
              </Typography>
              {isLoadingCameras ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </Box>
              ) : (
                cameraShortcuts.map((shortcut) => (
                  <Stack key={shortcut.command} direction="row" spacing={1} sx={{ mt: 1 }}>
                    <KeyboardCmd keys={shortcut.keys} />

                    <Box sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.875rem' }}>{shortcut.command}</Box>
                  </Stack>
                ))
              )}
            </Box>
          )}

          <Box sx={{ width: '225px', m: 2, mt: 4 }}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Mouse Controls
            </Typography>
            <MouseDiagram />
          </Box>
        </Stack>
      </DialogContent>

      <Divider sx={{ mx: 3, borderColor: 'rgba(255,255,255,0.12)' }} />

      <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 2, px: 2 }}>
        <Button
          onClick={hide}
          autoFocus
          variant="contained"
          sx={{
            width: 120,
            height: 32,
            minWidth: '120px',
            maxWidth: '180px',
            gap: '4px',
            opacity: 0.95,
            borderRadius: '100px',
            pt: '9px',
            pr: '16px',
            pb: '9px',
            pl: '16px',
            color: 'rgba(255,255,255,0.9)',
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.18)',
              boxShadow: 'none',
            },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeyboardShortcutsDialog;
